/**
 * ionKnowledgeService — admin-side wrappers around the
 * /api/v1/admin/ion-knowledge endpoints.
 *
 * Each function returns the parsed JSON on success and throws on
 * non-2xx so the calling component can `try/catch` around the network
 * boundary cleanly.
 */
import { authenticatedFetch } from './authService.js'
import { getApiBase } from '../components/admin/analytics/shared/formatters.js'

const BASE = () => `${getApiBase()}/admin/ion-knowledge`

async function jsonOrThrow(res) {
  if (res.ok) return await res.json()
  let detail = `HTTP ${res.status}`
  try {
    const body = await res.json()
    if (body?.detail) detail = body.detail
  } catch {
    /* not JSON — keep the status text */
  }
  throw new Error(detail)
}

export async function listKnowledgeEntries({
  includeInactive = true,
  appliesTo = null,
  category = null,
} = {}) {
  const params = new URLSearchParams({
    include_inactive: includeInactive ? 'true' : 'false',
  })
  // Optional branch/context filter → only entries whose applies_to tags the
  // slug as an exact token (powers the per-flow Knowledge tab).
  if (appliesTo && String(appliesTo).trim()) {
    params.set('applies_to', String(appliesTo).trim())
  }
  // Optional admin-plane category filter (selection-flow / domain-knowledge /
  // feature-doc / policy / general) — powers the Knowledge subtab's category
  // selector. Composable with appliesTo.
  if (category && String(category).trim()) {
    params.set('category', String(category).trim())
  }
  const res = await authenticatedFetch(`${BASE()}?${params}`)
  return jsonOrThrow(res)
}

/** The fixed admin-plane category taxonomy — drives the category <select>.
 *  Never used at runtime (the prompt index / get_knowledge don't read it). */
export async function listKnowledgeCategories() {
  const res = await authenticatedFetch(`${BASE()}/categories`)
  return jsonOrThrow(res)
}

export async function getKnowledgeEntry(topicId) {
  const res = await authenticatedFetch(`${BASE()}/${encodeURIComponent(topicId)}`)
  return jsonOrThrow(res)
}

export async function createKnowledgeEntry(payload) {
  const res = await authenticatedFetch(BASE(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return jsonOrThrow(res)
}

export async function updateKnowledgeEntry(topicId, patch) {
  const res = await authenticatedFetch(`${BASE()}/${encodeURIComponent(topicId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  return jsonOrThrow(res)
}

export async function deleteKnowledgeEntry(topicId) {
  const res = await authenticatedFetch(`${BASE()}/${encodeURIComponent(topicId)}`, {
    method: 'DELETE',
  })
  return jsonOrThrow(res)
}

// ---------------------------------------------------------------------------
// Approval gate (2026-06-22)
// ---------------------------------------------------------------------------

/** What the current admin may do: publish directly vs. queue, review the
 *  queue, and the pending count for the Review badge. */
export async function getKnowledgeCapabilities() {
  const res = await authenticatedFetch(`${BASE()}/capabilities`)
  return jsonOrThrow(res)
}

export async function listKnowledgeProposals({ status = 'pending' } = {}) {
  const params = new URLSearchParams({ status })
  const res = await authenticatedFetch(`${BASE()}/proposals?${params}`)
  return jsonOrThrow(res)
}

/** The caller's OWN submissions (any status), for the "My Submissions" view.
 *  Available to any knowledge author (admin or knowledge_expert) — no approver
 *  capability required. */
export async function listMyKnowledgeProposals() {
  const res = await authenticatedFetch(`${BASE()}/my-proposals`)
  return jsonOrThrow(res)
}

export async function approveKnowledgeProposal(proposalId, { force = false } = {}) {
  const params = new URLSearchParams(force ? { force: 'true' } : {})
  const qs = params.toString() ? `?${params}` : ''
  const res = await authenticatedFetch(`${BASE()}/proposals/${proposalId}/approve${qs}`, {
    method: 'POST',
  })
  return jsonOrThrow(res)
}

export async function rejectKnowledgeProposal(proposalId, reviewNote = '') {
  const res = await authenticatedFetch(`${BASE()}/proposals/${proposalId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ review_note: reviewNote }),
  })
  return jsonOrThrow(res)
}

export async function listKnowledgeEditors() {
  const res = await authenticatedFetch(`${BASE()}/editors`)
  return jsonOrThrow(res)
}

export async function setKnowledgeEditorFlags(userId, flags) {
  const res = await authenticatedFetch(`${BASE()}/editors/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(flags),
  })
  return jsonOrThrow(res)
}

// ---------------------------------------------------------------------------
// Cross-environment promotion (staging → prod)
//
// Knowledge lives per-environment — admin edits on staging never reach prod
// on their own. These two wrap the idempotent export/import API: dump the full
// bundle here, import it on the target (upsert by topic_id, never deletes).
// Both are ADMIN-only server-side.
// ---------------------------------------------------------------------------

/** Dump every entry (incl. inactive) as a portable JSON bundle:
 *  `{ schema, count, entries: [...] }`. */
export async function exportKnowledgeBundle() {
  const res = await authenticatedFetch(`${BASE()}/export`)
  return jsonOrThrow(res)
}

/** Upsert a bundle by topic_id. `entries` is the array from a prior export
 *  (the bundle's `.entries`, ≤1000). Returns `{ created, updated, total }`. */
export async function importKnowledgeBundle(entries) {
  const res = await authenticatedFetch(`${BASE()}/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entries }),
  })
  return jsonOrThrow(res)
}
